<script setup lang="ts">
import type { ISkuSelectPopupShowData } from '../models';
import type { TPdpProductEntity } from '@/entities';

import { MMPopupDefault, useMedia } from '@mm-frontend/mm-web-ui-kit';
import { SkuSelector } from '~/features/sku-selector';
import { snackBarManager } from '~/features/ui-kit';
import { useProductTracker } from '@/features/product-page/business/trackers';

import SkuSelectPopupDesktop from './SkuSelectPopupDesktop.vue';
import SkuSelectPopupMobile from './SkuSelectPopupMobile.vue';
import ButtonAddToCart from './ButtonAddToCart.vue';
import SkuPopupTitle from './SkuPopupTitle.vue';
import PriceBlock from './PriceBlock.vue';

const props = defineProps<{
  onShowData: ISkuSelectPopupShowData;
}>();

const emit = defineEmits<{ (event: 'close'): void }>();

const photosMap = new Map(props.onShowData.pdp.photos.map((it) => [it.key, it.high]));

const selectedSku = ref<TPdpProductEntity>();
const isLoading = ref(false);

const photoUrl = computed(() => {
  const defaultPhotoUrl = props.onShowData.pdp.photos[0]?.high;
  if (!selectedSku.value) return defaultPhotoUrl;

  const key = selectedSku.value.photo?.key;
  return photosMap.get(key) ?? defaultPhotoUrl;
});

const fullPrice = computed(() => selectedSku.value?.fullPrice ?? props.onShowData.pdp.minFullPrice);
const sellPrice = computed(() => selectedSku.value?.sellPrice ?? props.onShowData.pdp.minSellPrice);

const { trackMiniProductToCartClick } = useProductTracker();

// Helper function to prepare tracking parameters
const getTrackingParams = () => {
  if (!props.onShowData.pdp || !selectedSku.value) return {};

  const pdp = props.onShowData.pdp;
  const sku = selectedSku.value;

  // Prepare product parameters (selected characteristics from characteristicValues)
  const product_parameters =
    sku.characteristicValues?.reduce(
      (acc, charValue) => {
        acc[charValue.characteristicTitle] = charValue.label || charValue.value || '';
        return acc;
      },
      {} as Record<string, string>
    ) || {};

  // Prepare product parameters list (all available values from pdp.characteristics)
  const product_parameters_list =
    pdp.characteristics?.reduce(
      (acc, char) => {
        acc[char.title] = char.values?.map((opt) => opt.title || opt.value || '') || [];
        return acc;
      },
      {} as Record<string, string[]>
    ) || {};

  // Prepare is_picture_list (which photos exist)
  const is_picture_list = pdp.photos?.map(() => true) || [];

  // Prepare badges string
  const badges = pdp.badges?.map((badge) => badge.text).join(';') || '';

  // Get source from history state, then document.referrer, fallback to 'unknown'
  const getSource = () => {
    if (typeof window !== 'undefined') {
      // Try to get from history state (if available)
      if (window.history.state && typeof window.history.state === 'object') {
        const historyState = window.history.state;
        // Check if there's referrer info in history state
        if (historyState.referrer && historyState.referrer.trim() !== '') {
          return historyState.referrer;
        }
        if (historyState.from && historyState.from.trim() !== '') {
          return historyState.from;
        }
        if (historyState.source && historyState.source.trim() !== '') {
          return historyState.source;
        }
      }

      // Try document.referrer as fallback
      if (document.referrer && document.referrer.trim() !== '') {
        return document.referrer;
      }
    }

    return 'unknown';
  };

  // Get referer from document.referrer or props
  const getReferer = () => {
    if (props.onShowData.referer) {
      return props.onShowData.referer;
    }
    if (typeof window !== 'undefined' && document.referrer && document.referrer.trim() !== '') {
      return document.referrer;
    }
    return undefined;
  };

  return {
    product_parameters,
    product_parameters_list,
    rating_value: sku.rating,
    source: getSource(),
    referer: getReferer(),
    plid: props.onShowData.plid,
    is_picture_list,
    old_price: (sku.fullPrice || 0) / 100, // Convert from kopecks to rubles
    price: (sku.sellPrice || 0) / 100, // Convert from kopecks to rubles
    rev_num: sku.feedbackQuantity || 0,
    wishlist_toggle: sku.isFavorite || false,
    badges,
    shop_id: sku.shop?.id,
    is_parsing_rating: sku.externalRating || false,
    badges_id: pdp.badges?.map((badge) => badge.id).join(',') || '', // All badge IDs as comma-separated string
    clickable_badges: false, // IPdp badges don't have clickable property
  };
};

function onAddToCart() {
  if (!selectedSku.value) {
    snackBarManager.showSnackBar({ message: 'Выберите опции' });
    return;
  }

  // Track the add to cart event for mini product screen
  // Note: IPdp doesn't have id, so we use skuId as product identifier
  trackMiniProductToCartClick(
    selectedSku.value.id?.toString() || selectedSku.value.skuId?.toString() || '',
    selectedSku.value.skuId?.toString() || '',
    getTrackingParams()
  );

  isLoading.value = true;
  props.onShowData
    .addToCartFn(selectedSku.value)
    .then(() => {
      // TODO Заменить после релиза на popup
      snackBarManager.showSnackBar({
        type: 'success',
        message: 'Товар добавлен в корзину',
        dismissible: true,
      });
      emit('close');
    })
    .catch(() => {
      snackBarManager.showSnackBar({
        type: 'error',
        message: 'Не удалось добавить товар в корзину',
      });
    })
    .finally(() => (isLoading.value = false));
}

const { isMobileOrTablet } = useMedia();

const isPricePrefixShown = computed(() => {
  if (selectedSku.value) return false;

  const { pdp } = props.onShowData;
  return pdp.skuList.reduce((acc, it) => it.sellPrice !== pdp.minSellPrice || acc, false);
});
</script>

<template>
  <MMPopupDefault is-closable class="sku-select-popup" @close="$emit('close')">
    <template #default>
      <component :is="isMobileOrTablet ? SkuSelectPopupMobile : SkuSelectPopupDesktop">
        <template #img>
          <img :src="photoUrl" class="sku-select-popup__img" />
        </template>

        <template #title>
          <SkuPopupTitle :title="onShowData.pdp.title" :badges="onShowData.pdp.badges" />
        </template>

        <template #sku-selectors>
          <SkuSelector
            v-model="selectedSku"
            :characteristics="onShowData.pdp.characteristics"
            :products="onShowData.pdp.skuList"
            :loading="isLoading"
          />
        </template>

        <template #price-block>
          <PriceBlock
            :full-price="fullPrice"
            :sell-price="sellPrice"
            :is-prefix-shown="isPricePrefixShown"
          />
        </template>

        <template #button>
          <ButtonAddToCart
            :available-amount="selectedSku?.availableAmount"
            :nearest-delivery="onShowData.pdp.nearestDelivery"
            :loading="isLoading"
            @click="onAddToCart"
          />
        </template>
      </component>
    </template>
  </MMPopupDefault>
</template>

<style scoped lang="stylus">
.sku-select-popup
  word-break break-word

  &__img
    width 100%
    aspect-ratio 3/4
    background-color #EFEFEF
    border-radius 10px
    object-fit contain
    position sticky
    top 0
</style>
